/**
 * NewsTab - News articles and sentiment display for Research page
 */
import { useState } from 'react'
import { useNewsForSymbol, useNewsSummary } from '../../hooks/useNews'
import type { NewsArticleListItem, SentimentType } from '../../types/news'

interface NewsTabProps {
  symbol: string
}

type TimeFilter = 7 | 30 | 90 | 365

const TIME_FILTER_OPTIONS: { value: TimeFilter; label: string }[] = [
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
  { value: 365, label: '1 Year' },
]

// =============================================================================
// Helper Components
// =============================================================================

function SummaryStat({
  label,
  value,
  variant = 'default',
}: {
  label: string
  value: string | number
  variant?: 'default' | 'positive' | 'negative' | 'neutral'
}) {
  const variantStyles = {
    default: 'text-gray-900',
    positive: 'text-green-600',
    negative: 'text-red-600',
    neutral: 'text-gray-600',
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 text-center">
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className={`text-2xl font-semibold ${variantStyles[variant]}`}>{value}</p>
    </div>
  )
}

function SentimentBadge({ sentiment }: { sentiment: SentimentType | null }) {
  if (!sentiment) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
        Unanalyzed
      </span>
    )
  }

  const styles: Record<SentimentType, string> = {
    positive: 'bg-green-100 text-green-800',
    negative: 'bg-red-100 text-red-800',
    neutral: 'bg-gray-100 text-gray-600',
  }

  const labels: Record<SentimentType, string> = {
    positive: 'Positive',
    negative: 'Negative',
    neutral: 'Neutral',
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[sentiment]}`}
    >
      {labels[sentiment]}
    </span>
  )
}

function MaterialBadge() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
      Material
    </span>
  )
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffHours / 24)

  if (diffHours < 1) {
    return 'Just now'
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

function ArticleSkeleton() {
  return (
    <div className="p-4 rounded-lg border border-gray-200 bg-white animate-pulse">
      <div className="flex gap-4">
        <div className="flex-shrink-0 w-20 h-20 bg-gray-200 rounded" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-3/4 bg-gray-200 rounded" />
          <div className="h-4 w-1/2 bg-gray-200 rounded" />
          <div className="flex gap-2 mt-2">
            <div className="h-5 w-16 bg-gray-200 rounded" />
            <div className="h-5 w-20 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Article Row Component
// =============================================================================

function ArticleRow({ article }: { article: NewsArticleListItem }) {
  const getSentimentBorderColor = (sentiment: SentimentType | null): string => {
    if (!sentiment) return 'border-gray-200'
    const colors: Record<SentimentType, string> = {
      positive: 'border-green-300',
      negative: 'border-red-300',
      neutral: 'border-gray-300',
    }
    return colors[sentiment]
  }

  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`block p-4 rounded-lg border-2 bg-white hover:bg-gray-50 transition-colors ${
        article.is_material ? 'border-amber-300' : getSentimentBorderColor(article.sentiment)
      }`}
    >
      <div className="flex gap-4">
        {/* Thumbnail */}
        {article.image_url && (
          <div className="flex-shrink-0 w-20 h-20 rounded overflow-hidden bg-gray-100">
            <img
              src={article.image_url}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide broken images
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Headline */}
          <h3 className="font-medium text-gray-900 line-clamp-2 hover:text-primary-600">
            {article.headline}
          </h3>

          {/* Metadata row */}
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            <span className="font-medium">{article.source}</span>
            <span>-</span>
            <span>{formatTimeAgo(article.published_at)}</span>
          </div>

          {/* Badges row */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <SentimentBadge sentiment={article.sentiment} />
            {article.is_material && <MaterialBadge />}
            {article.sentiment_score !== null && (
              <span className="text-xs text-gray-500">
                Score: {article.sentiment_score.toFixed(2)}
              </span>
            )}
          </div>

          {/* AI Summary (if available) */}
          {article.ai_summary && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2">
              {article.ai_summary}
            </p>
          )}
        </div>
      </div>
    </a>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function NewsTab({ symbol }: NewsTabProps) {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(30)

  const {
    data: summary,
    isLoading: summaryLoading,
  } = useNewsSummary(symbol, timeFilter)

  const {
    data: newsData,
    isLoading: newsLoading,
    error,
  } = useNewsForSymbol(symbol, {
    days: timeFilter,
    limit: 100,
  })

  const articles = newsData?.articles ?? []
  const isLoading = summaryLoading || newsLoading

  return (
    <div className="space-y-6">
      {/* Time filter buttons */}
      <div className="flex flex-wrap gap-2">
        {TIME_FILTER_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setTimeFilter(option.value)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              timeFilter === option.value
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Summary stats */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-4 animate-pulse">
              <div className="h-4 w-20 bg-gray-200 rounded mx-auto mb-2" />
              <div className="h-8 w-16 bg-gray-200 rounded mx-auto" />
            </div>
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryStat
            label="Total Articles"
            value={summary.total_articles}
          />
          <SummaryStat
            label="Positive"
            value={summary.positive_count}
            variant="positive"
          />
          <SummaryStat
            label="Negative"
            value={summary.negative_count}
            variant="negative"
          />
          <SummaryStat
            label="Material News"
            value={summary.material_count}
            variant="default"
          />
        </div>
      ) : null}

      {/* Error state */}
      {error && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-600">
          No news data available for this symbol.
        </div>
      )}

      {/* Loading state for articles */}
      {newsLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <ArticleSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Articles list */}
      {!isLoading && !error && (
        <>
          {articles.length > 0 ? (
            <div className="space-y-3">
              {articles.map((article) => (
                <ArticleRow key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                />
              </svg>
              <h3 className="mt-4 text-sm font-medium text-gray-900">No news articles</h3>
              <p className="mt-2 text-sm text-gray-500">
                No news articles found for {symbol} in the last {timeFilter} days.
                Try selecting a longer time period.
              </p>
            </div>
          )}

          {/* Results count */}
          {articles.length > 0 && (
            <p className="text-sm text-gray-500 text-center">
              Showing {articles.length} of {newsData?.total ?? articles.length} article{articles.length !== 1 ? 's' : ''}
              {' '}from the last {timeFilter} days
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default NewsTab
